
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Helmet } from "react-helmet";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

const Login = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    // Get the return path from query parameters or local storage
    const params = new URLSearchParams(location.search);
    const returnTo = params.get("returnTo");
    if (returnTo) {
      console.log(`Found returnTo parameter: ${returnTo}`);
      // Store the return path in local storage for use after authentication
      localStorage.setItem("auth_redirect_path", returnTo);
    }
    
    // Check if already logged in via Supabase
    if (user) {
      console.log("Already logged in via AuthContext, redirecting to dashboard");
      const redirectPath = localStorage.getItem("auth_redirect_path") || "/admin/dashboard";
      navigate(redirectPath);
    }

    // Check if we need to handle hash fragment from Supabase auth
    if (window.location.hash && window.location.hash.length > 0) {
      // This means we've been redirected back from Supabase auth
      console.log("Detected auth redirect with hash, processing authentication");
      setIsProcessingAuth(true);
      handleAuthRedirect();
    }
  }, [location, navigate, user]);

  // Process the auth redirect and handle domain switching if needed
  const handleAuthRedirect = async () => {
    try {
      console.log("Processing auth redirect...");
      
      // Process the hash parameters by manually setting the session
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error("Error getting session:", error);
        throw error;
      }
      
      if (data.session) {
        // Get the redirect path (default to dashboard if not found)
        const redirectPath = localStorage.getItem("auth_redirect_path") || "/admin/dashboard";
        
        console.log(`Auth successful, redirecting to: ${redirectPath}`);
        
        // Navigate to the stored path
        toast.success("Login successful!");
        navigate(redirectPath);
      } else {
        // No session found, try to handle the hash directly
        console.log("No session found after auth redirect, trying to set session from hash");
        
        // Extract the hash without the leading #
        const hashParams = window.location.hash.substring(1);
        if (hashParams && hashParams.includes("access_token")) {
          try {
            // Parse the hash string into an object
            const params = new URLSearchParams(hashParams);
            const sessionData = {
              access_token: params.get("access_token") || "",
              refresh_token: params.get("refresh_token") || ""
            };
            
            // Only proceed if we have an access token
            if (sessionData.access_token) {
              console.log("Parsed access token from hash, attempting to set session");
              
              // Try to set the session from the parsed parameters
              const { data, error } = await supabase.auth.setSession(sessionData);
              
              if (error) {
                console.error("Error setting session from hash:", error);
                throw error;
              }
              
              if (data.session) {
                console.log("Session set successfully from hash");
                
                // Get the redirect path (default to dashboard if not found)
                const redirectPath = localStorage.getItem("auth_redirect_path") || "/admin/dashboard";
                
                console.log(`Auth successful from hash, redirecting to: ${redirectPath}`);
                
                // Navigate to the stored path
                toast.success("Login successful!");
                navigate(redirectPath);
                return;
              }
            } else {
              console.error("No access token found in hash parameters");
            }
          } catch (hashError) {
            console.error("Error handling hash params:", hashError);
          }
        }
        
        console.log("Authentication failed - no session found after all attempts");
        setError("Authentication failed. Please try again.");
      }
    } catch (err) {
      console.error("Error processing auth redirect:", err);
      setError(err instanceof Error ? err.message : "Failed to process authentication");
    } finally {
      setIsProcessingAuth(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    setError("");
    try {
      // Store the current path for post-auth redirect
      const params = new URLSearchParams(location.search);
      const returnTo = params.get("returnTo") || "/admin/dashboard";
      localStorage.setItem("auth_redirect_path", returnTo);
      
      console.log(`Storing redirect path: ${returnTo}`);
      
      // Get the current URL's origin for the redirectTo
      const currentOrigin = window.location.origin;
      const redirectUrl = `${currentOrigin}/admin/login`;
      
      console.log(`Using redirect URL: ${redirectUrl}`);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo: redirectUrl,
          scopes: 'email profile'
        }
      });

      if (error) {
        setError(error.message);
        toast.error(error.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unknown error occurred");
      toast.error(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (isProcessingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-edgefi-purple/30 border-t-edgefi-purple rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Processing authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Helmet>
        <title>Admin Login | edgefi</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <img 
              src="/lovable-uploads/3faf62f0-dd43-43cc-82c2-9329f89d269f.png" 
              alt="edgefi Logo" 
              className="h-10"
            />
          </div>
          <CardTitle className="text-2xl text-center">Admin Login</CardTitle>
          <CardDescription className="text-center">
            Sign in with your Microsoft 365 account
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="grid gap-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <Button 
              onClick={handleMicrosoftLogin} 
              className="w-full bg-[#2F2F2F] hover:bg-[#1F1F1F] text-white" 
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign in with Microsoft 365"}
            </Button>

            <div className="text-xs text-gray-500 text-center mt-4">
              {process.env.NODE_ENV === 'development' && (
                <>
                  <div>Current URL: {window.location.href}</div>
                  <div className="mt-1">Hash: {window.location.hash}</div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
